'use client';

import { useToast } from '@/containers/ToastMessage';
import { selectAccountDetails, selectAppUser } from '@/lib/redux/app.selectors';
import { minutesToTimeObject, minutesToTimeString } from '@/shared/utils/time';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useClearCanvassers,
  useRequestCanvassers,
  useUpdateJobDates,
  useUpdateScheduleForCanvassers,
} from '../api';
import { CanvasserScheduleFormData } from '../components/canvassers/types';
import { JobDetailsResponse } from '../types';
import { useJobDetails } from './useJobDetails';
import { useSchedule } from './useSchedule';

export const useCanvassers = (jobId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const accountDetails = useSelector(selectAccountDetails);
  const appUser = useSelector(selectAppUser);
  const { data: jobDetails } = useJobDetails(jobId);
  const { data: scheduleDetails } = useSchedule(jobDetails?.schedule_id || 0);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Individual API hooks
  const { mutateAsync: updateSchedule } = useUpdateScheduleForCanvassers();
  const { mutateAsync: updateJobDates } = useUpdateJobDates();
  const { mutateAsync: requestCanvassers } = useRequestCanvassers();
  const { mutateAsync: clearCanvassers } = useClearCanvassers();

  // Multi-step canvasser request sequence
  const submitCanvasserRequest = useCallback(
    async (formData: CanvasserScheduleFormData) => {
      if (!jobDetails || !scheduleDetails || !accountDetails) {
        showError('Missing required job or account information');
        return;
      }

      setIsSubmitting(true);

      try {
        const {
          timing,
          requestedDate,
          timeframe,
          timezone,
          startTime,
          endTime,
          userInitials,
        } = formData;

        // Step 1: Determine if we need to update schedule
        const needsScheduleUpdate = timing === 'future' && timeframe === 'custom';

        if (needsScheduleUpdate) {
          // Update schedule with new timezone and time windows
          const schedulePayload = {
            account: accountDetails.account_id,
            schedule_id: scheduleDetails.schedule_id,
            schedule_name: scheduleDetails.schedule_name,
            schedule_timezone: timezone,
            is_global: scheduleDetails.is_global === 1,
            // Apply the time window to all days of the week
            sun_start: minutesToTimeObject(startTime),
            sun_end: minutesToTimeObject(endTime),
            mon_start: minutesToTimeObject(startTime),
            mon_end: minutesToTimeObject(endTime),
            tue_start: minutesToTimeObject(startTime),
            tue_end: minutesToTimeObject(endTime),
            wed_start: minutesToTimeObject(startTime),
            wed_end: minutesToTimeObject(endTime),
            thu_start: minutesToTimeObject(startTime),
            thu_end: minutesToTimeObject(endTime),
            fri_start: minutesToTimeObject(startTime),
            fri_end: minutesToTimeObject(endTime),
            sat_start: minutesToTimeObject(startTime),
            sat_end: minutesToTimeObject(endTime),
          };

          await updateSchedule({
            scheduleId: scheduleDetails.schedule_id,
            scheduleData: schedulePayload,
          });
        }

        // Step 2: Update job with new dates (if future date is selected)
        if (timing === 'future' && requestedDate) {
          await updateJobDates({
            jobId,
            jobData: {
              start_date: requestedDate,
              end_date: requestedDate,
              schedule_id: scheduleDetails.schedule_id,
            },
          });
        }

        // Step 3: Submit canvasser request
        const canvasserPayload = {
          requested_date:
            timing === 'asap' ? new Date().toISOString().split('T')[0] : requestedDate,
          requested_timeframe: timing === 'asap' ? 'ASAP' : timeframe,
          requested_start_time: minutesToTimeString(startTime),
          requested_end_time: minutesToTimeString(endTime),
          requested_timezone: timezone,
          requested_initials: userInitials,
        };

        // Step 3A: Optimistically update job data locally
        queryClient.setQueryData(
          ['jobDetails', jobId],
          (oldData: JobDetailsResponse | undefined) => {
            if (!oldData) return oldData;

            // Create optimistic canvassers_schedule based on form data
            const optimisticSchedule = {
              requested_at: new Date().toISOString(),
              requested_date:
                timing === 'asap'
                  ? new Date().toISOString().split('T')[0]
                  : requestedDate,
              requested_start_time: minutesToTimeString(startTime),
              requested_end_time: minutesToTimeString(endTime),
              requested_timezone: timezone,
              requested_initials: userInitials,
              approved: false,
              requested_by: appUser?.email || '',
              requested_timeframe: timing === 'asap' ? 'ASAP' : timeframe,
              start_time: '', // Will be populated by API
              end_time: '', // Will be populated by API
            };

            return {
              ...oldData,
              canvassers_schedule: optimisticSchedule,
              canvassers_schedule_status: 'pending' as const,
              has_canvassers_scheduled: true,
            };
          }
        );

        await requestCanvassers({
          jobId,
          canvasserData: canvasserPayload,
        });

        // Step 4: Refresh data to get actual API response
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['jobDetails', jobId] }),
          queryClient.invalidateQueries({
            queryKey: ['schedule', scheduleDetails.schedule_id],
          }),
        ]);

        // Show success message
        const timeframeText = timing === 'asap' ? 'ASAP' : timeframe;
        const dateText =
          timing === 'asap' ? 'today' : new Date(requestedDate).toLocaleDateString();
        showSuccess(
          `Your request for initial canvassers for ${timeframeText} ${dateText} has been submitted for review.`
        );
      } catch (error) {
        console.error('Error submitting canvasser request:', error);

        // Handle specific error case mentioned in FRD
        const axiosError = error as AxiosError<{ message?: string }>;
        if (
          axiosError?.response?.data?.message?.includes(
            'requested_date cannot be before job start_date'
          )
        ) {
          showError(
            'You cannot request canvassers prior to the start date of your job. Please adjust the start date in the Delivery Schedule section of this job and save or select a later date for canvassing.'
          );
        } else {
          const errorMessage =
            axiosError?.response?.data?.message ||
            (error as Error)?.message ||
            'Failed to submit canvasser request. Please try again.';
          showError(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      jobDetails,
      scheduleDetails,
      accountDetails,
      appUser,
      updateSchedule,
      updateJobDates,
      requestCanvassers,
      queryClient,
      showSuccess,
      showError,
      jobId,
    ]
  );

  // Clear canvassers sequence
  const clearCanvasserSchedule = useCallback(async () => {
    if (!jobDetails) {
      showError('Job details not available');
      return;
    }

    try {
      await clearCanvassers({ jobId });

      // Refresh job data
      await queryClient.invalidateQueries({ queryKey: ['jobDetails', jobId] });

      showSuccess('Canvasser schedule has been cleared successfully.');
    } catch (error) {
      console.error('Error clearing canvasser schedule:', error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError?.response?.data?.message ||
        (error as Error)?.message ||
        'Failed to clear canvasser schedule. Please try again.';
      showError(errorMessage);
    }
  }, [jobDetails, clearCanvassers, queryClient, showSuccess, showError, jobId]);

  return {
    submitCanvasserRequest,
    clearCanvasserSchedule,
    isSubmitting,
  };
};
